import SEO from "../components/SEO";
import Reveal from "../components/Reveal";
import Breadcrumbs from "../components/Breadcrumbs";
import FrontLoadForm from "../components/forms/FrontLoadForm";
import FAQAccordion from "../components/FAQAccordion";
import CTASection from "../components/CTASection";
import DumpsterCard from "../components/DumpsterCard";
import { FRONTLOAD_SIZES } from "../data/site";

const CONFIG = {
  residential: {
    title: "Residential Front-Load Service",
    headline: "Residential Front-Load Service in Yavapai County",
    intro: "Reliable recurring waste service for your home or property. Branded 3–8 yard front-load dumpsters on a schedule that fits — weekly, multiple times per week, or custom.",
    faqPage: "frontload-residential",
    path: "/front-load/residential",
    defaultType: "Residential",
    image: "/assets/yws/uploads/front-load-single.jpeg",
    points: [
      { title: "Flexible Schedules", text: "Weekly, multiple times per week, or a custom schedule based on your waste volume." },
      { title: "Right-Sized Service", text: "3, 4, 6, and 8 yard dumpsters — we'll help you pick the right fit." },
      { title: "Real Local People", text: "Call (928) 713-2710 and reach the team that actually runs your route." },
    ],
  },
  commercial: {
    title: "Commercial Front-Load Service",
    headline: "Commercial Front-Load Service for Local Businesses",
    intro: "Flexible waste service for businesses across Yavapai County. Branded 3–8 yard dumpsters, sized to your volume and emptied on your schedule.",
    faqPage: "frontload-commercial",
    path: "/front-load/commercial",
    defaultType: "Commercial",
    image: "/assets/yws/uploads/fl-truck-dumping-commercial.jpeg",
    points: [
      { title: "Schedules That Flex", text: "Weekly, multiple times per week, or custom — adjusted as your business changes." },
      { title: "Sized To Your Volume", text: "From small offices to high-traffic storefronts: 3, 4, 6, and 8 yard options." },
      { title: "Straightforward Pricing", text: "No surprise fees. No games. Talk to a real local team about your service." },
    ],
  },
};

export default function FrontLoadService({ kind }) {
  const c = CONFIG[kind];
  return (
    <>
      <SEO title={c.title} description={c.intro} path={c.path} />
      <section className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-14">
          <Breadcrumbs items={[{ label: "Home", to: "/" }, { label: "Front-Load Service", to: "/front-load" }, { label: c.title }]} />
          <div className="mt-8 grid lg:grid-cols-2 gap-10 items-center">
            <div>
              <h1 className="font-heading font-extrabold tracking-tight text-4xl sm:text-5xl text-slate-900 leading-[1.05]">{c.headline}</h1>
              <p className="mt-4 text-lg text-slate-600 leading-relaxed">{c.intro}</p>
              <ul className="mt-7 space-y-4">
                {c.points.map((p) => (
                  <li key={p.title} className="flex gap-4">
                    <span className="h-2 w-2 rounded-full bg-sky-600 mt-2.5 shrink-0" aria-hidden="true" />
                    <div>
                      <h2 className="font-heading font-semibold text-slate-900">{p.title}</h2>
                      <p className="text-sm text-slate-600">{p.text}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
            <img src={c.image} alt={`Branded YWS front-load dumpsters for ${kind} service`} className="rounded-2xl w-full aspect-[16/10] object-cover shadow-xl" data-testid={`${kind}-hero-image`} />
          </div>
        </div>
      </section>

      <section className="py-14 bg-slate-50 border-b border-slate-200" data-testid={`${kind}-sizes`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <h2 className="font-heading font-bold text-2xl sm:text-3xl text-slate-900 tracking-tight">Front-Load Sizes</h2>
          </Reveal>
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {FRONTLOAD_SIZES.map((s, i) => (
              <Reveal key={s.size} delay={i * 0.06}>
                <DumpsterCard
                  {...s}
                  frontload
                  image="/assets/yws/uploads/frontload-render.jpg"
                  imageAlt={`YWS ${s.size}-yard front-load dumpster with Yavapai Waste Solutions branding`}
                />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-white" data-testid={`${kind}-form-section`}>
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="text-center mb-8">
            <h2 className="font-heading font-bold text-3xl text-slate-900 tracking-tight">Request Front-Load Service</h2>
            <p className="mt-3 text-slate-600">About a minute. We'll confirm size, placement, and schedule.</p>
          </Reveal>
          <FrontLoadForm defaultType={c.defaultType} />
        </div>
      </section>

      <section className="py-16 bg-slate-50 border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <FAQAccordion page={c.faqPage} />
        </div>
      </section>

      <CTASection title="Talk to a Real Person About Service" subtitle="Call (928) 713-2710, Monday–Friday 8 AM–5 PM. Or request service online anytime." />
    </>
  );
}
